import {
    Button,
    Checkbox,
    HTMLSelect,
    InputGroup,
    Intent,
    Popover
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { segmentTrackCompareScenariosOpenFilter } from "../../analytics";
import css from "./CompareFilters.module.scss";
import {
    DEFAULT_ROW_CHECKS,
    DiffType,
    RowChecks,
    RowCounts
} from "./comparisons";

export type SelectOption = { value: string; label: string };

// Subset of ScopeProps that CompareFilters needs to render the scope dropdown.
// Compare's generic `ScopeProps<TRow>` extends this with an `apply` filter.
export type ScopeFilterProps = {
    selected: string;
    setScope: (scope: string) => void;
    options: ReadonlyArray<SelectOption>;
};

type FacetSubOption = {
    diffType: DiffType;
    label: string;
    intent: Intent;
};

const CHANGE_SUB_OPTIONS: ReadonlyArray<FacetSubOption> = [
    { diffType: DiffType.DecreaseInB, label: "Decreased", intent: "success" },
    { diffType: DiffType.IncreaseInB, label: "Increased", intent: "danger" },
    { diffType: DiffType.Unchanged, label: "Unchanged", intent: "none" }
];

const PRESENCE_SUB_OPTIONS: ReadonlyArray<FacetSubOption> = [
    { diffType: DiffType.OnlyA, label: "A only", intent: "primary" },
    { diffType: DiffType.OnlyB, label: "B only", intent: "warning" }
];

const buildSubOptions = (
    showPresence: boolean
): ReadonlyArray<FacetSubOption> =>
    showPresence
        ? [...CHANGE_SUB_OPTIONS, ...PRESENCE_SUB_OPTIONS]
        : CHANGE_SUB_OPTIONS;

const formatCount = (n: number): string => n.toLocaleString();

const Dot: React.FC<{ intent: Intent; off?: boolean }> = ({ intent, off }) => (
    <span
        className={css["dot"]}
        data-intent={intent}
        data-off={off ? "true" : undefined}
    />
);

const FacetButton: React.FC<{
    label: string;
    subOptions: ReadonlyArray<FacetSubOption>;
    rowChecks: RowChecks;
    setRowChecks: (update: (prev: RowChecks) => RowChecks) => void;
    rowCounts: RowCounts;
}> = ({ label, subOptions, rowChecks, setRowChecks, rowCounts }) => {
    const activeCount = subOptions.filter((o) => rowChecks[o.diffType]).length;
    const isDirty = activeCount > 0;

    const toggleOne = (diffType: DiffType) =>
        setRowChecks((prev) => ({ ...prev, [diffType]: !prev[diffType] }));

    const setAll = (val: boolean) =>
        setRowChecks((prev) => {
            const next = { ...prev };
            subOptions.forEach((o) => {
                next[o.diffType] = val;
            });
            return next;
        });

    const popoverContent = (
        <div className={css["popover"]}>
            <div className={css["popover-header"]}>
                <span className={css["popover-title"]}>{label}</span>
                <Button
                    className={css["popover-header-button"]}
                    variant="minimal"
                    size="small"
                    intent="primary"
                    onClick={() => setAll(true)}
                >
                    All
                </Button>
                <Button
                    className={css["popover-header-button"]}
                    variant="minimal"
                    size="small"
                    intent="primary"
                    onClick={() => setAll(false)}
                >
                    None
                </Button>
            </div>
            {subOptions.map((o) => (
                <Checkbox
                    key={o.diffType}
                    className={css["popover-row"]}
                    checked={rowChecks[o.diffType]}
                    onChange={() => toggleOne(o.diffType)}
                >
                    <span className={css["popover-row-content"]}>
                        <Dot intent={o.intent} />
                        <span>{o.label}</span>
                        <span className={css["popover-row-count"]}>
                            {formatCount(rowCounts[o.diffType])}
                        </span>
                    </span>
                </Checkbox>
            ))}
        </div>
    );

    return (
        <Popover
            content={popoverContent}
            placement="bottom-start"
            minimal
            modifiers={{
                offset: { enabled: true, options: { offset: [0, 4] } }
            }}
            onOpened={segmentTrackCompareScenariosOpenFilter}
        >
            <Button
                className={css["facet-button"]}
                data-dirty={isDirty ? "true" : undefined}
                endIcon={IconNames.CHEVRON_DOWN}
                intent={isDirty ? "primary" : "none"}
                variant="outlined"
            >
                <span className={css["facet-button-inner"]}>
                    <span className={css["facet-button-label"]}>{label}</span>
                    <span className={css["facet-button-dots"]}>
                        {subOptions.map((o) => (
                            <Dot
                                key={o.diffType}
                                intent={o.intent}
                                off={isDirty && !rowChecks[o.diffType]}
                            />
                        ))}
                    </span>
                    {isDirty && (
                        <span className={css["facet-button-badge"]}>
                            {activeCount}
                        </span>
                    )}
                </span>
            </Button>
        </Popover>
    );
};

const isAnyDirty = (
    rowChecks: RowChecks,
    subOptions: ReadonlyArray<FacetSubOption>
): boolean => subOptions.some((o) => rowChecks[o.diffType]);

export const CompareFilters: React.FC<{
    scope: ScopeFilterProps | null;
    rowChecks: RowChecks;
    setRowChecks: (update: (prev: RowChecks) => RowChecks) => void;
    showPresenceFilter: boolean;
    rowCounts: RowCounts;
    filteredColumn: string;
    setFilteredColumn: (colId: string) => void;
    columnOptions: ReadonlyArray<SelectOption>;
    filterText: string;
    setFilterText: (s: string) => void;
    rendered: number;
    /** `undefined` while the full row set is still loading. */
    total: number | undefined;
    itemLabel: string;
    onExport?: () => void;
}> = ({
    scope,
    rowChecks,
    setRowChecks,
    showPresenceFilter,
    rowCounts,
    filteredColumn,
    setFilteredColumn,
    columnOptions,
    filterText,
    setFilterText,
    rendered,
    total,
    itemLabel,
    onExport
}) => {
    const subOptions = buildSubOptions(showPresenceFilter);
    const showClearAll = isAnyDirty(rowChecks, subOptions);

    const clearAll = () => setRowChecks(() => ({ ...DEFAULT_ROW_CHECKS }));

    return (
        <div className={css["controls-bar"]}>
            {scope && (
                <HTMLSelect
                    className={css["dropdown"]}
                    value={scope.selected}
                    onChange={(e) => scope.setScope(e.currentTarget.value)}
                    options={[...scope.options]}
                />
            )}
            <div className={css["separator"]} />
            <HTMLSelect
                className={css["dropdown-small"]}
                value={filteredColumn}
                onChange={(e) => setFilteredColumn(e.currentTarget.value)}
                options={[...columnOptions]}
            />
            <FacetButton
                label="Change"
                subOptions={subOptions}
                rowChecks={rowChecks}
                setRowChecks={setRowChecks}
                rowCounts={rowCounts}
            />
            <div className={css["spacer"]} />
            {showClearAll && (
                <Button
                    className={css["clear-all"]}
                    variant="minimal"
                    size="small"
                    icon={IconNames.CROSS}
                    onClick={clearAll}
                >
                    Clear all
                </Button>
            )}
            <InputGroup
                className={css["search-input"]}
                type="search"
                leftIcon={IconNames.SEARCH}
                placeholder={`Filter by ${itemLabel}...`}
                value={filterText}
                onChange={(e) => setFilterText(e.currentTarget.value)}
                rightElement={
                    filterText ? (
                        <Button
                            variant="minimal"
                            size="small"
                            icon={IconNames.CROSS}
                            onClick={() => setFilterText("")}
                        />
                    ) : undefined
                }
            />
            {onExport && (
                <Button icon={IconNames.EXPORT} onClick={onExport}>
                    Export displayed
                </Button>
            )}

            <div className={css["status"]}>
                <span
                    className={css["status-rendered"]}
                    style={{
                        minWidth: `${formatCount(total ?? rendered).length}ch`
                    }}
                >
                    {formatCount(rendered)}
                </span>{" "}
                {total === undefined
                    ? itemLabel
                    : `of ${formatCount(total)} ${itemLabel}`}
            </div>
        </div>
    );
};
